/**
 * Leave It Sorted — Homepage Section JS
 * ----------------------------------------------------------------
 * - Cart drawer (slide-in from right) + FAB (floating action button)
 * - All CTAs with [data-lis-buy] add to cart + open drawer
 * - Hero CTA has [data-lis-scroll] instead → smooth-scroll to #buy
 * - Currency handled by Shopify Markets (cart.currency + Intl.NumberFormat)
 * - Optional cross-sell block inside the drawer
 * ---------------------------------------------------------------- */

(function () {
  'use strict';

  const wrapper = document.querySelector('.lis-homepage, .lis-advertorial');
  if (!wrapper) return;

  const t = {
    empty: 'Your cart is empty.',
    subtotal: 'Subtotal',
    checkout: 'Proceed to checkout — Instant Download',
    remove: 'Remove',
    adding: 'Adding…',
    processing: 'Processing…',
    error: 'Something went wrong. Please try again.'
  };

  // ============================================
  // Smooth-scroll for hero CTA and any [data-lis-scroll]
  // ============================================
  wrapper.querySelectorAll('a[data-lis-scroll], a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const targetId = link.getAttribute('href');
      if (!targetId || targetId === '#') return;
      const target = document.querySelector(targetId);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (history.pushState) history.pushState(null, '', targetId);
    });
  });

  // ============================================
  // FAQ accordion — single open at a time
  // ============================================
  const faqItems = wrapper.querySelectorAll('.faq-item');
  faqItems.forEach((item) => {
    item.addEventListener('toggle', () => {
      if (item.open) {
        faqItems.forEach((other) => { if (other !== item && other.open) other.open = false; });
      }
    });
  });

  // ============================================
  // Helpers
  // ============================================
  function escapeHtml(str) {
    if (typeof str !== 'string') return String(str == null ? '' : str);
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function formatMoney(cents, currency) {
    const lang = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(lang, { style: 'currency', currency: currency || 'EUR' })
        .format((cents || 0) / 100);
    } catch (e) {
      return `${((cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  }
  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  async function fetchCart() {
    const r = await fetch(shopifyRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } });
    if (!r.ok) throw new Error('cart_fetch_failed');
    return await r.json();
  }
  async function addToCart(variantId) {
    const r = await fetch(shopifyRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.description || 'add_failed');
    }
    return await r.json();
  }
  function setBusy(btn, busy, label) {
    if (busy) {
      btn.dataset.busy = '1';
      btn.dataset.originalHtml = btn.innerHTML;
      btn.setAttribute('aria-busy', 'true');
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.7';
      if (label) btn.innerHTML = label;
    } else {
      if (btn.dataset.originalHtml) btn.innerHTML = btn.dataset.originalHtml;
      btn.removeAttribute('aria-busy');
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
      btn.dataset.busy = '0';
    }
  }

  // ============================================
  // Drawer references
  // ============================================
  const drawer = wrapper.querySelector('.lis-cart-drawer');
  const backdrop = wrapper.querySelector('.lis-cart-drawer-backdrop');
  const fab = wrapper.querySelector('.lis-cart-fab');
  const drawerBody = drawer && drawer.querySelector('.lis-cart-drawer-body');
  const drawerFooter = drawer && drawer.querySelector('.lis-cart-drawer-footer');
  const closeBtn = drawer && drawer.querySelector('.lis-cart-drawer-close');
  const fabBadge = fab && fab.querySelector('.lis-cart-fab-badge');
  let lastFocusedEl = null;

  function renderDrawer(cart) {
    if (!drawerBody || !drawerFooter) return;
    const currency = cart.currency || 'EUR';
    if (!cart.item_count || cart.item_count === 0) {
      drawerBody.innerHTML = '<div class="lis-cart-empty">' + t.empty + '</div>';
      drawerFooter.style.display = 'none';
      return;
    }
    let itemsHtml = '';
    cart.items.forEach((item) => {
      const imgSrc = item.image || (item.featured_image && item.featured_image.url) || '';
      const productTitle = escapeHtml(item.product_title || item.title || '');
      const variant = escapeHtml(item.variant_title || '');
      const key = escapeHtml(item.key);
      const qty = parseInt(item.quantity, 10) || 1;
      itemsHtml +=
        '<div class="lis-cart-item" data-line-key="' + key + '">' +
          '<div class="lis-cart-item-image">' +
            (imgSrc ? '<img src="' + escapeHtml(imgSrc) + '" alt="' + productTitle + '" loading="lazy">' : '') +
          '</div>' +
          '<div class="lis-cart-item-info">' +
            '<div class="lis-cart-item-title">' + productTitle + '</div>' +
            (variant ? '<div class="lis-cart-item-variant">' + variant + '</div>' : '') +
            '<div class="lis-cart-qty-row">' +
              '<div class="lis-cart-qty" role="group" aria-label="Quantity">' +
                '<button type="button" data-qty-dec data-line-key="' + key + '" data-qty="' + qty + '" aria-label="Decrease quantity">−</button>' +
                '<span class="qty" data-qty-display>' + qty + '</span>' +
                '<button type="button" data-qty-inc data-line-key="' + key + '" data-qty="' + qty + '" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button class="lis-cart-item-remove" type="button" data-line-key="' + key + '">' + t.remove + '</button>' +
            '</div>' +
          '</div>' +
          '<div class="lis-cart-item-price">' + formatMoney(item.line_price, currency) + '</div>' +
        '</div>';
    });
    drawerBody.innerHTML = itemsHtml;
    const pmSrc = drawer.getAttribute('data-payment-methods-src');
    const paymentsHtml = pmSrc
      ? '<div class="lis-cart-payment-methods">' +
          '<img src="' + escapeHtml(pmSrc) + '" alt="Accepted payment methods" loading="lazy">' +
        '</div>'
      : '';
    drawerFooter.innerHTML =
      '<div class="lis-cart-subtotal">' +
        '<span>' + t.subtotal + '</span>' +
        '<span class="lis-cart-subtotal-amount">' + formatMoney(cart.total_price, currency) + '</span>' +
      '</div>' +
      '<a href="' + shopifyRoot() + 'checkout" class="lis-cart-checkout-btn">' + t.checkout + '</a>' +
      paymentsHtml;
    drawerFooter.style.display = 'block';
    // Wire remove buttons
    drawerBody.querySelectorAll('.lis-cart-item-remove').forEach((btn) => {
      btn.addEventListener('click', () => changeQty(btn.dataset.lineKey, 0));
    });
    // Wire quantity steppers
    drawerBody.querySelectorAll('[data-qty-dec]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const current = parseInt(btn.dataset.qty, 10) || 1;
        changeQty(btn.dataset.lineKey, Math.max(0, current - 1), btn);
      });
    });
    drawerBody.querySelectorAll('[data-qty-inc]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const current = parseInt(btn.dataset.qty, 10) || 1;
        changeQty(btn.dataset.lineKey, current + 1, btn);
      });
    });
  }

  function updateFab(cart, pulse) {
    if (!fab || !fabBadge) return;
    if (cart && cart.item_count > 0) {
      fab.classList.add('is-visible');
      fabBadge.textContent = cart.item_count;
      fabBadge.style.display = 'flex';
      if (pulse) {
        fab.classList.remove('lis-cart-fab--pulse');
        void fab.offsetWidth;
        fab.classList.add('lis-cart-fab--pulse');
        setTimeout(() => fab.classList.remove('lis-cart-fab--pulse'), 700);
      }
    } else {
      fab.classList.remove('is-visible');
    }
  }

  function openDrawer() {
    if (!drawer || !backdrop) return;
    lastFocusedEl = document.activeElement;
    drawer.classList.add('is-open');
    backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    setTimeout(() => { if (closeBtn) closeBtn.focus(); }, 100);
  }
  function closeDrawer() {
    if (!drawer || !backdrop) return;
    drawer.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocusedEl && lastFocusedEl.focus) lastFocusedEl.focus();
  }

  async function changeQty(lineKey, newQty, sourceBtn) {
    // Optimistic UI: mark stepper busy, disable both buttons on this row
    if (sourceBtn) {
      sourceBtn.setAttribute('aria-busy', 'true');
      sourceBtn.setAttribute('disabled', 'disabled');
    }
    try {
      const r = await fetch(shopifyRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: newQty })
      });
      if (!r.ok) throw new Error('change_failed');
      const cart = await r.json();
      renderDrawer(cart);
      updateFab(cart);
    } catch (e) {
      console.error('[LIS Cart] Change quantity failed:', e);
      if (sourceBtn) {
        sourceBtn.removeAttribute('aria-busy');
        sourceBtn.removeAttribute('disabled');
      }
    }
  }

  async function handleAddToDrawer(btn, e) {
    e.preventDefault();
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) {
      // No product configured — scroll to buy section as safety fallback
      const buy = document.getElementById('buy');
      if (buy) buy.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (btn.dataset.busy === '1') return;
    setBusy(btn, true, t.adding);
    try {
      await addToCart(variantId);
      const cart = await fetchCart();
      renderDrawer(cart);
      updateFab(cart, true);
      openDrawer();
    } catch (err) {
      console.error('[LIS Cart] Add failed:', err);
      alert(t.error);
    } finally {
      setBusy(btn, false);
    }
  }

  // ============================================
  // Wire CTAs
  // ============================================
  wrapper.querySelectorAll('[data-lis-buy]').forEach((btn) => {
    btn.addEventListener('click', (e) => handleAddToDrawer(btn, e));
  });

  if (drawer && fab) {
    fab.addEventListener('click', async () => {
      try {
        const cart = await fetchCart();
        renderDrawer(cart);
        openDrawer();
      } catch (e) { console.error('[LIS Cart] Fetch on FAB failed:', e); }
    });
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });
    // Initial FAB state (persisted cart from previous visit)
    fetchCart().then((cart) => updateFab(cart)).catch(() => {});
  }
})();
